import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside } from '@theme/utilities';

/** Viewport width below which the drawer opens as a modal overlay (no squeeze). */
const MODAL_BREAKPOINT = 990;

/** Restore the focus trap to the highest open desktop drawer. */
function restoreTopFocusTrap() {
  const openDrawers = /** @type {ThemeDrawer[]} */ (Array.from(document.querySelectorAll('theme-drawer[open]')));
  if (openDrawers.some((drawer) => drawer.refs.panel.matches(':modal'))) return;

  openDrawers.sort(
    (a, b) =>
      Number(a.style.getPropertyValue('--drawer-stack-order') || 0) -
      Number(b.style.getPropertyValue('--drawer-stack-order') || 0)
  );
  const topDrawer = openDrawers.at(-1);
  if (topDrawer) trapFocus(topDrawer.refs.panel);
}

/**
 * Opens a dialog in the requested mode, switching modes when necessary.
 *
 * @param {HTMLDialogElement} panel
 * @param {boolean} modal
 */
function syncDialogMode(panel, modal) {
  if (panel.open && modal !== panel.matches(':modal')) panel.close();
  if (!panel.open) modal ? panel.showModal() : panel.show();
}

/**
 * Resolves when the drawer's transform settles, including interrupted transitions.
 *
 * @param {HTMLDialogElement} panel
 * @returns {Promise<void>}
 */
function waitForDrawerTransition(panel) {
  const durations = getComputedStyle(panel).transitionDuration.split(',');
  if (durations.every((duration) => Number.parseFloat(duration) === 0)) return Promise.resolve();

  return new Promise((resolve) => {
    /** @param {TransitionEvent} [event] */
    const finish = (event) => {
      if (event && (event.target !== panel || event.propertyName !== 'transform')) return;

      clearTimeout(timeout);
      panel.removeEventListener('transitionend', finish);
      panel.removeEventListener('transitioncancel', onCancel);
      resolve();
    };
    /** @param {TransitionEvent} event */
    const onCancel = (event) => {
      if (panel.classList.contains('theme-drawer__dialog--closing')) return;
      finish(event);
    };
    const timeout = setTimeout(finish, 300);
    panel.addEventListener('transitionend', finish);
    panel.addEventListener('transitioncancel', onCancel);
  });
}

/**
 * A drawer that opens from the right side.
 *
 * On wide viewports (≥ 990px) the drawer squeezes page content alongside it.
 * The panel is a non-modal dialog (`show()`); we install a focus trap via
 * `trapFocus()` so Tab cycles within the drawer, mirroring the modal-mode
 * a11y contract. Focus moves to the close button on open and returns to
 * the trigger on close.
 *
 * On narrow viewports (< 990px) the drawer overlays with a backdrop. The
 * panel is a modal dialog (`showModal()`) — native focus trap, scroll-lock,
 * and ARIA semantics. Same focus-on-close-button + restore-on-close UX.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The dialog that owns all visual styles and animation.
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** Invalidates an in-flight close when the drawer reopens. */
  #closeVersion = 0;

  /** Element to restore focus to when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`);

  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#modalQuery.addEventListener('change', this.#onModalBreakpointChange);

    // Sync the static stack counter with any --drawer-stack-order set by the
    // synchronous restore script (which runs before this module loads).
    const restoredOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restore path: the inline script in theme-drawer.liquid set [open] on
   * <theme-drawer> + <dialog> before this module loaded. The dialog is
   * already visible — we just wire close listeners. We deliberately skip
   * trapFocus and focus moves: the user is loading a fresh page and
   * expects focus on main content, not inside a drawer left over from
   * the previous session.
   */
  #onRestore() {
    const { panel } = this.refs;
    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    const restoreTrap = this.isOpen && this.#isTopmost();
    super.disconnectedCallback();
    if (this.isOpen && this.#modalQuery.matches && !document.querySelector('theme-drawer[open]')) {
      document.documentElement.removeAttribute('scroll-lock');
    }
    this.#modalQuery.removeEventListener('change', this.#onModalBreakpointChange);
    this.#removeEventListeners();
    removeTrapFocus(this.refs.panel);
    if (restoreTrap) restoreTopFocusTrap();
    this.#closeVersion += 1;
  }

  /**
   * Closes the drawer when the user clicks the backdrop (outside the dialog
   * content area). Only meaningful in modal mode — in sidebar mode the
   * dialog has no backdrop so this handler is inert.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (isClickedOutside(event, panel)) {
      this.close();
    }
  };

  /**
   * Switches the dialog between modal and non-modal when the viewport
   * crosses the modal breakpoint while the drawer is open.
   */
  #onModalBreakpointChange = () => {
    if (!this.isOpen) return;

    const { panel } = this.refs;

    // Switch modes without animation — the drawer stays visually in place.
    syncDialogMode(panel, this.#modalQuery.matches);
    removeTrapFocus(panel);

    if (this.#modalQuery.matches) {
      document.documentElement.setAttribute('scroll-lock', '');
      requestAnimationFrame(() => {
        if (document.querySelectorAll('theme-drawer[open]').length < 2) return;
        if (!this.isOpen || !this.#modalQuery.matches || !this.#isTopmost()) return;
        if (!panel.open || !panel.matches(':modal')) return;

        panel.close();
        panel.showModal();
        this.#focusInitialElement();
      });
    } else {
      document.documentElement.removeAttribute('scroll-lock');
      restoreTopFocusTrap();
    }

    if (this.#isTopmost()) this.#focusInitialElement();

    // Ensure keydown and click listeners are registered. The sessionStorage
    // restore path bypasses open(), so these may not have been added yet.
    // addEventListener deduplicates identical listeners, so this is safe
    // even if they were already registered.
    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  };

  /**
   * Closes the drawer when the user presses Escape.
   * preventDefault() suppresses the native dialog cancel event in modal mode.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    // isComposing: don't close while an Input Method Editor (e.g. Japanese,
    // Chinese, Korean input) consumes Escape to dismiss its candidate list —
    // can happen inside the cart's discount-code field.
    // defaultPrevented: let nested components handle Escape first.
    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction. `'inline-start'` slides left-to-right (used when the chat docks).
   */
  open({ enter } = {}) {
    const { panel } = this.refs;

    if (this.#isClosing) {
      this.#closeVersion += 1;
      this.#isClosing = false;
      panel.classList.remove('theme-drawer__dialog--closing');

      const refreshModalStack = panel.open && this.#modalQuery.matches && panel.matches(':modal');
      syncDialogMode(panel, this.#modalQuery.matches);
      if (refreshModalStack) {
        panel.close();
        panel.showModal();
      }

      ThemeDrawer.#stackOrder += 1;
      this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

      this.setAttribute('open', '');

      if (this.#modalQuery.matches) {
        document.documentElement.setAttribute('scroll-lock', '');
      } else {
        trapFocus(panel);
      }

      this.#focusInitialElement();
      document.addEventListener('keydown', this.#onKeyDown);
      panel.addEventListener('click', this.#onBackdropClick);
      this.dispatchEvent(new DrawerOpenEvent());
      return;
    }

    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    if (this.#modalQuery.matches) {
      document.documentElement.setAttribute('scroll-lock', '');
      panel.showModal();
    } else {
      panel.show();
      trapFocus(panel);
    }

    this.#focusInitialElement();

    requestAnimationFrame(() => panel.classList.remove(openClass));

    this.setAttribute('open', '');
    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  /**
   * Bumps this drawer's z-index to the top of the stack and replays
   * the slide-in transition. Called when open() is invoked on an
   * already-open drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction.
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    // In modal mode, dialogs live in the browser's top layer where z-index
    // is ignored — stacking follows showModal() call order. Re-calling
    // showModal() moves this dialog to the top of the stack.
    if (this.#modalQuery.matches) {
      if (panel.open) {
        panel.close();
        panel.showModal();
      }
    } else {
      trapFocus(panel);
    }

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);
    requestAnimationFrame(() => panel.classList.remove(openClass));

    // Re-fire so persistDrawerState picks up the new stack order.
    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener('keydown', this.#onKeyDown);
    panel.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Moves focus to the close button inside the drawer panel.
   * Every theme-drawer renders the close button via theme-drawer-header.
   * If a future drawer omits it, that drawer is responsible for its own focus management.
   */
  #focusInitialElement() {
    const closeButton = /** @type {HTMLElement | null} */ (
      this.refs.panel.querySelector('.theme-drawer__close-button')
    );
    closeButton?.focus();
  }

  /**
   * Slides the drawer out, waits for the transition, then closes the dialog.
   */
  async #closeDialog() {
    if (!this.isOpen) return;
    if (this.#isClosing) return;
    this.#isClosing = true;
    const closeVersion = this.#closeVersion;
    const restoreTrap = this.#isTopmost();
    const { panel } = this.refs;

    this.#removeEventListeners();
    removeTrapFocus(panel);

    this.removeAttribute('open');
    this.dispatchEvent(new DrawerCloseEvent());

    if (closeVersion !== this.#closeVersion) return;
    if (restoreTrap) restoreTopFocusTrap();

    // Only remove scroll-lock if this was the last open modal drawer.
    if (this.#modalQuery.matches && !document.querySelector('theme-drawer[open]')) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    if (panel.open) {
      // Cancel any in-progress open transition before starting the close.
      panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');

      panel.classList.add('theme-drawer__dialog--closing');
      await waitForDrawerTransition(panel);
      if (closeVersion !== this.#closeVersion) return;
      panel.classList.remove('theme-drawer__dialog--closing');
    }

    panel.close();
    this.style.removeProperty('--drawer-stack-order');

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;
    // A Section Rendering API morph between open and close can replace the
    // trigger node. The JS reference stays valid but the element is detached
    // from the live DOM — and .focus() on a detached node is a silent no-op,
    // so we explicitly check and fall back to a fresh query.
    if (trigger && document.contains(trigger)) {
      trigger.focus();
    } else {
      /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${this.id}"]`))?.focus();
    }

    this.#isClosing = false;
  }

  /**
   * Closes the drawer.
   */
  async close() {
    if (this.#isClosing) return;
    await this.#closeDialog();
  }

  /**
   * Toggles the drawer. If the drawer is open but not the topmost in the
   * stack, it is brought to front instead of closed.
   */
  toggle() {
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }
    return this.open();
  }

  /**
   * @returns {boolean} Whether this drawer has the highest stack order among all open drawers.
   */
  #isTopmost() {
    const myOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    for (const other of document.querySelectorAll('theme-drawer[open]')) {
      if (other === this) continue;
      const otherOrder = Number(/** @type {HTMLElement} */ (other).style.getPropertyValue('--drawer-stack-order') || 0);
      if (otherOrder > myOrder) return false;
    }
    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:close';
}
